import { useRef, useEffect } from 'react';

import { useAccountContext } from 'application/providers/AccountProvider';

import { CompanyTagQueryFilter, CompanyTagQueryPagination } from 'domain/api/graphql/generated';
import { DEFAULT_PAGINATION_INFO, DEFAULT_MEDIUM_PAGINATION } from 'application/pages/constants';

import {
  COMPANY_TAGS_PAGINATION_DEFAULT_FILTERS,
  UseCompanyTagsPaginationPresenterProps,
  UseCompanyTagsPaginationPresenter,
} from './types';

const useCompanyTagsPaginationPresenter = ({
  getCompanyTags,
  getMoreCompanyTags,
  readCompanyTags,
}: UseCompanyTagsPaginationPresenterProps): UseCompanyTagsPaginationPresenter => {
  const { currentCompany } = useAccountContext();

  const filtersRef = useRef<CompanyTagQueryFilter>({
    ...COMPANY_TAGS_PAGINATION_DEFAULT_FILTERS,
    companyId: currentCompany!.id,
  });

  const paginationRef = useRef<CompanyTagQueryPagination>(DEFAULT_MEDIUM_PAGINATION);

  const allCompanyTagsCount = useRef<null | number>(null);

  const cachedCompanyTags = readCompanyTags({
    filters: filtersRef.current,
    pagination: paginationRef.current,
  });

  const resetFilters = () => {
    filtersRef.current = {
      ...COMPANY_TAGS_PAGINATION_DEFAULT_FILTERS,
      companyId: currentCompany!.id,
    };

    paginationRef.current = DEFAULT_MEDIUM_PAGINATION;
  };

  const requestCompanyTags = async (
    filters: CompanyTagQueryFilter = COMPANY_TAGS_PAGINATION_DEFAULT_FILTERS,
    pagination: CompanyTagQueryPagination = DEFAULT_MEDIUM_PAGINATION
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyId: currentCompany!.id,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    await getCompanyTags({
      filters: filtersRef.current,
      pagination: paginationRef.current,
    });
  };

  const loadMoreCompanyTags = async () => {
    const page = (cachedCompanyTags?.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await getMoreCompanyTags({
      filters: filtersRef.current,
      pagination: paginationRef.current,
    });
  };

  const onFilterExecute = async (newFilters: CompanyTagQueryFilter) => {
    const preparedFilters: CompanyTagQueryFilter = {
      companyId: currentCompany!.id,
    };

    const { title } = newFilters;

    if (title !== undefined) {
      preparedFilters.title = title;
      filtersRef.current.title = title;
    }

    if (preparedFilters.title === undefined && filtersRef.current.title) {
      preparedFilters.title = filtersRef.current.title;
    }

    await getCompanyTags({
      filters: preparedFilters,
      pagination: DEFAULT_MEDIUM_PAGINATION,
    });
  };

  useEffect(() => {
    if (
      cachedCompanyTags?.paginationInfo?.totalCount !== undefined &&
      (allCompanyTagsCount.current === null || allCompanyTagsCount.current === 0)
    ) {
      allCompanyTagsCount.current = cachedCompanyTags.paginationInfo?.totalCount || 0;
    }
  }, [cachedCompanyTags?.paginationInfo]);

  return {
    companyTags: cachedCompanyTags?.result || [],
    paginationInfo: cachedCompanyTags?.paginationInfo || DEFAULT_PAGINATION_INFO,
    allCompanyTagsCount: allCompanyTagsCount.current,
    filtersRef,
    requestCompanyTags,
    loadMoreCompanyTags,
    resetFilters,
    onFilterExecute,
  };
};

export default useCompanyTagsPaginationPresenter;
